import { useEffect, useRef } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { SolidStatusPill } from '@odo/shared-ui';
import type { IteroOrder } from '../../../../../services/iteroBrowse';
import { BROWSE_FORMATTERS } from '../browseFormatters';
import { CARD_CLASS } from '../browserStyles';
import { timelineMeta } from './profileFormat';

interface TimelineCardProps {
    order: IteroOrder;
    autoFocus: boolean;
    onOpen: (order: IteroOrder) => void;
}

export function TimelineCard({ order, autoFocus, onOpen }: TimelineCardProps) {
    const cardRef = useRef<HTMLButtonElement>(null);
    const focusOnMount = useRef(autoFocus);
    const procedure = order.procedureName ?? order.procedureTypeName;
    const meta = timelineMeta(order, BROWSE_FORMATTERS);

    useEffect(() => {
        if (focusOnMount.current) cardRef.current?.focus();
    }, []);

    return (
        <button
            ref={cardRef}
            type="button"
            onClick={() => onOpen(order)}
            className={`${CARD_CLASS} group flex w-full items-center gap-3 px-3.5 py-3 text-left transition-[border-color,box-shadow] hover:border-gray-300 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400`}
        >
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span
                    className={`truncate text-[13px] font-semibold leading-[18px] ${procedure ? 'text-gray-900' : 'text-gray-400'}`}
                >
                    {procedure ?? 'Procedure not recorded'}
                </span>
                {meta && <span className="truncate text-[12px] text-gray-500">{meta}</span>}
            </span>
            <span className="max-w-[140px] shrink-0">
                <SolidStatusPill value={order.status} compact />
            </span>
            <ChevronRightIcon
                className="h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover:text-gray-700"
                aria-hidden="true"
            />
        </button>
    );
}
